import React, { useEffect, useMemo, useRef, useState } from "react";
import { URL } from "../../utils/constants";
import "./OCKCloud.css";

const ROOT_PATH = "OCK";

function normalizeSlashes(value) {
  return String(value || "")
    .replace(/\\/g, "/")
    .replace(/\/+/g, "/")
    .replace(/^\/|\/$/g, "");
}

function splitPath(path) {
  const normalized = normalizeSlashes(path);
  return normalized ? normalized.split("/") : [];
}

function joinPath(parts) {
  return normalizeSlashes(parts.join("/"));
}

function getParentPath(path) {
  const parts = splitPath(path);
  if (parts.length <= 1) return ROOT_PATH;
  return joinPath(parts.slice(0, -1));
}

function getBaseName(path) {
  const parts = splitPath(path);
  return parts[parts.length - 1] || "";
}

function buildObjectFolderName(object) {
  if (object?.cloud_folder_name) {
    return normalizeSlashes(object.cloud_folder_name);
  }

  if (object?.id && object?.name) {
    return normalizeSlashes(`${object.id}_${object.name}`);
  }

  return normalizeSlashes(String(object?.name || "").trim());
}

function buildObjectFolderPath(object) {
  if (object?.cloud_folder_path) {
    return normalizeSlashes(object.cloud_folder_path);
  }

  return joinPath([ROOT_PATH, buildObjectFolderName(object)]);
}

function buildBreadcrumbs(path) {
  const parts = splitPath(path);
  return parts.map((part, idx) => ({
    name: part,
    path: joinPath(parts.slice(0, idx + 1)),
  }));
}

function buildApiUrl(pathOrUrl) {
  const value = String(pathOrUrl || "").trim();

  if (!value) return "";

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  const base = String(URL || "").replace(/\/+$/g, "");
  const path = value.startsWith("/") ? value : `/${value}`;

  return `${base}${path}`;
}

function isDirectChild(parentPath, childPath) {
  const parent = splitPath(parentPath);
  const child = splitPath(childPath);

  if (child.length !== parent.length + 1) return false;
  return parent.every((part, idx) => child[idx] === part);
}

function buildFolderTreeFromObjects(objects) {
  const objectFolders = (objects || [])
    .map((obj) => {
      const fullPath = buildObjectFolderPath(obj);
      const folderName = getBaseName(fullPath);

      return {
        type: "folder",
        id: `folder:${fullPath}`,
        name: folderName,
        displayName: obj.name || folderName,
        path: fullPath,
        objectId: obj.id,
        children: [],
      };
    })
    .sort((a, b) => String(a.displayName || a.name).localeCompare(String(b.displayName || b.name), "ru"));

  return {
    root: {
      type: "folder",
      id: `folder:${ROOT_PATH}`,
      name: ROOT_PATH,
      path: ROOT_PATH,
      children: objectFolders,
    },
    flatFolders: [
      {
        type: "folder",
        id: `folder:${ROOT_PATH}`,
        name: ROOT_PATH,
        path: ROOT_PATH,
      },
      ...objectFolders,
    ],
  };
}

function getDirectoryEntries(currentPath, docLinks, folders) {
  const current = normalizeSlashes(currentPath);

  const childFolders = folders
    .filter((f) => isDirectChild(current, f.path))
    .map((f) => ({
      type: "folder",
      id: `folder:${f.path}`,
      name: f.displayName || f.name,
      path: f.path,
      updated_at: "",
      uploaded_by_username: "",
      size: null,
      objectId: f.objectId ?? null,
    }));

  const currentFolder = folders.find(
    (f) => normalizeSlashes(f.path) === current
  );

  const currentObjectId = currentFolder?.objectId ?? null;

  const childFiles = currentObjectId
    ? docLinks
        .filter((link) => String(link.object_id) === String(currentObjectId))
        .map((link) => {
          const name = link.name || link.display_name || link.document_name || "Без названия";
          const virtualPath = joinPath([current, name]);

          return {
            type: "file",
            id: `link:${link.link_id || link.id}`,
            linkId: link.link_id || link.id,
            docId: link.document_id,
            objectId: link.object_id,
            name,
            path: virtualPath,
            previewUrl: link.preview_url,
            downloadUrl: link.download_url,
            canPreview: link.can_preview,
            updated_at: link.updated_at,
            created_at: link.created_at,
            uploaded_by_username: link.uploaded_by_username,
            uploaded_by_id: link.uploaded_by_id,
            size: link.size ?? null,
            raw: link,
          };
        })
    : [];

  return [...childFolders, ...childFiles].sort((a, b) => {
    if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
    return String(a.name || "").localeCompare(String(b.name || ""), "ru");
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function formatSize(size) {
  if (size === null || size === undefined || Number.isNaN(Number(size))) return "—";
  const bytes = Number(size);

  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} ГБ`;
}

function getFilenameFromContentDisposition(header) {
  if (!header) return "";

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const normalMatch = header.match(/filename="?([^"]+)"?/i);
  return normalMatch?.[1] || "";
}

function getPreviewMimeType(entry, response) {
  const headerType = response.headers.get("Content-Type");

  if (headerType && !headerType.includes("application/octet-stream")) {
    return headerType;
  }

  const name = String(entry.name || entry.raw?.document_name || "").toLowerCase();
  const ext = String(entry.raw?.file_ext || "").toLowerCase();

  const value = ext || name.slice(name.lastIndexOf("."));

  const map = {
    ".pdf": "application/pdf",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".png": "image/png",
    ".webp": "image/webp",
    ".txt": "text/plain;charset=utf-8",
  };

  return map[value] || headerType || "application/octet-stream";
}

function FolderTreeNode({
  node,
  currentPath,
  expandedPaths,
  onToggle,
  onOpen,
  level = 0,
}) {
  const isActive = normalizeSlashes(node.path) === normalizeSlashes(currentPath);
  const isExpanded = expandedPaths.has(node.path);
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  return (
    <div className="ock-cloud__treeNode">
      <div
        className={`ock-cloud__treeRow ${isActive ? "ock-cloud__treeRow--active" : ""}`}
        style={{ paddingLeft: `${level * 14}px` }}
      >
        <button
          type="button"
          className="ock-cloud__treeToggle"
          onClick={() => hasChildren && onToggle(node.path)}
          disabled={!hasChildren}
          title={hasChildren ? "Развернуть / свернуть" : ""}
        >
          {hasChildren ? (isExpanded ? "▾" : "▸") : "•"}
        </button>

        <button
          type="button"
          className="ock-cloud__treeOpen"
          onClick={() => onOpen(node.path)}
          title={node.path}
        >
          📁 {node.name}
        </button>
      </div>

      {hasChildren && isExpanded ? (
        <div className="ock-cloud__treeChildren">
          {node.children.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              currentPath={currentPath}
              expandedPaths={expandedPaths}
              onToggle={onToggle}
              onOpen={onOpen}
              level={level + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function OCKCloud({ user, tokenProp }) {
  const [docLinks, setDocLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canUsePanel, setCanUsePanel] = useState(false);

  const [query, setQuery] = useState("");
  const [currentPath, setCurrentPath] = useState(ROOT_PATH);
  const [expandedPaths, setExpandedPaths] = useState(new Set([ROOT_PATH]));
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [objects, setObjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const canView = canUsePanel;
  const canManage = canUsePanel;

  const getToken = () => tokenProp || localStorage.getItem("auth_token");

  useEffect(() => {
    let cancelled = false;

    const loadObjects = async () => {
      const token = tokenProp || localStorage.getItem("auth_token");
      if (!token || !canView) return;

      try {
        const res = await fetch(`${URL}/api/ock-objects/`, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) {
          throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
        }

        const items = Array.isArray(data) ? data : data.items || data.results || [];

        const normalizedItems = items.filter(
          (obj) => String(obj.name || "").trim() !== ""
        );

        if (!cancelled) setObjects(normalizedItems);
      } catch (e) {
        console.error("Failed to load OCK objects:", e);
        if (!cancelled) setObjects([]);
      }
    };

    if (canView) loadObjects();

    return () => {
      cancelled = true;
    };
  }, [canView, tokenProp]);

  useEffect(() => {
    let mounted = true;

    const norm = (s) => String(s ?? "").toLowerCase().trim();

    const applyFromObj = (u = {}) => {
      let groups = u.groups || u.group_names || [];

      if (Array.isArray(groups) && groups.length && typeof groups[0] === "object") {
        groups = groups.map((g) => g?.name);
      }

      const normalizedGroups = (groups || []).map(norm);
      const hasOCK = normalizedGroups.includes("ock");

      if (mounted) setCanUsePanel(hasOCK);
      return hasOCK;
    };

    try {
      const raw = localStorage.getItem("user");
      const parsed = JSON.parse(raw || "{}");
      if (parsed && (parsed.id || parsed.pk || (parsed.user && parsed.user.id))) {
        applyFromObj(parsed);
      }
    } catch (e) {
      console.warn("Не удалось прочитать user из localStorage:", e);
    }

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      if (mounted) setLoading(false);
      return () => {
        mounted = false;
      };
    }

    const endpoints = [
      `${URL}/api/me/`,
      `${URL}/api/users/me/`,
      `${URL}/users/me/`,
      `${URL}/api/auth/users/me/`,
    ];

    (async () => {
      for (const ep of endpoints) {
        try {
          const r = await fetch(ep, {
            headers: { Authorization: `Token ${token}` },
          });

          if (!r.ok) continue;

          const data = await r.json();
          applyFromObj(data);
          break;
        } catch (e) {
          console.warn("Ошибка получения профиля:", ep, e);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [tokenProp, user]);

  useEffect(() => {
    let cancelled = false;

    const loadDocLinks = async () => {
      const token = tokenProp || localStorage.getItem("auth_token");
      if (!token || !canView) {
        if (!cancelled) setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const res = await fetch(`${URL}/api/ock-object-doc-links/?is_archived=false`, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) {
          throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
        }

        const items = Array.isArray(data) ? data : data.items || data.results || [];
        if (!cancelled) setDocLinks(items);
      } catch (e) {
        console.error("Failed to load OCK document links:", e);
        if (!cancelled) setDocLinks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (canView) {
      loadDocLinks();
    } else {
      setLoading(false);
      setDocLinks([]);
    }

    return () => {
      cancelled = true;
    };
  }, [canView, tokenProp]);

  const { root: folderTree, flatFolders } = useMemo(
    () => buildFolderTreeFromObjects(objects),
    [objects]
  );

  const breadcrumbs = useMemo(() => buildBreadcrumbs(currentPath), [currentPath]);

  const currentObject = useMemo(() => {
    const normalizedPath = normalizeSlashes(currentPath);

    const folder = flatFolders.find(
      (f) => normalizeSlashes(f.path) === normalizedPath
    );

    if (!folder?.objectId) return null;

    return objects.find((obj) => String(obj.id) === String(folder.objectId)) || null;
  }, [objects, flatFolders, currentPath]);

  const currentObjectId = currentObject?.id ?? null;
  const canUploadHere = Boolean(currentObject);

  const openUploadDialog = () => {
    if (!canUploadHere) {
      alert("Загрузка доступна только внутри папки конкретного объекта OCK");
      return;
    }
  
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const allEntries = useMemo(
    () => getDirectoryEntries(currentPath, docLinks, flatFolders),
    [currentPath, docLinks, flatFolders]
  );

  const filteredEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allEntries;

    return allEntries.filter((entry) => {
      const name = String(entry.name || "").toLowerCase();
      const path = String(entry.path || "").toLowerCase();
      return name.includes(q) || path.includes(q);
    });
  }, [allEntries, query]);

  const selectedEntries = useMemo(
    () => filteredEntries.filter((entry) => selectedIds.has(entry.id)),
    [filteredEntries, selectedIds]
  );

  const visibleFiles = useMemo(
    () => filteredEntries.filter((entry) => entry.type === "file"),
    [filteredEntries]
  );

  const allVisibleSelected =
    visibleFiles.length > 0 && visibleFiles.every((entry) => selectedIds.has(entry.id));

  const toggleExpand = (path) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const openFolder = (path) => {
    const normalized = normalizeSlashes(path);
    setCurrentPath(normalized);
    setExpandedPaths((prev) => new Set([...prev, normalized, getParentPath(normalized)]));
    setSelectedIds(new Set());
  };

  const goUp = () => {
    if (normalizeSlashes(currentPath) === ROOT_PATH) return;
    openFolder(getParentPath(currentPath));
  };

  const clearSearch = () => setQuery("");

  const toggleSelect = (entryId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (allVisibleSelected) {
        filteredEntries.forEach((entry) => next.delete(entry.id));
      } else {
        filteredEntries.forEach((entry) => next.add(entry.id));
      }

      return next;
    });
  };

  const handlePreview = async (entry) => {
    if (entry.type !== "file") return;

    if (!entry.docId) {
      alert("Не удалось определить ID документа для просмотра");
      console.error("Preview failed: empty docId", entry);
      return;
    }

    const token = getToken();
    if (!token) {
      alert("Нет токена авторизации");
      return;
    }

    try {
      const finalUrl = buildApiUrl(`/api/ock-object-docs/${entry.docId}/preview/`);

      console.log("Preview URL:", finalUrl, entry);

      const res = await fetch(finalUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      const rawBlob = await res.blob();
      const mimeType = getPreviewMimeType(entry, res);

      const previewBlob = new Blob([rawBlob], {
        type: mimeType,
      });

      const blobUrl = window.URL.createObjectURL(previewBlob);

      window.open(blobUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60_000);
    } catch (e) {
      console.error("Preview failed:", e);
      alert("Ошибка просмотра файла");
    }
  };

  const handleDownload = async (entry) => {
    if (entry.type !== "file") return;

    if (!entry.docId) {
      alert("Не удалось определить ID документа для скачивания");
      console.error("Download failed: empty docId", entry);
      return;
    }

    const token = getToken();
    if (!token) {
      alert("Нет токена авторизации");
      return;
    }

    try {
      const finalUrl = buildApiUrl(`/api/ock-object-docs/${entry.docId}/download/`);

      console.log("Download URL:", finalUrl, entry);

      const res = await fetch(finalUrl, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      const blob = await res.blob();

      const headerFilename = getFilenameFromContentDisposition(
        res.headers.get("Content-Disposition")
      );

      const filename = headerFilename || entry.name || "document";

      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error("Download failed:", e);
      alert("Ошибка скачивания файла");
    }
  };

  const handleDeleteSingle = async (entry) => {
    if (!canManage || entry.type !== "file") return;

    const ok = window.confirm(
      `Удалить файл "${entry.name}" в корзину? Он исчезнет из всех объектов, где прикреплен.`
    );
    if (!ok) return;

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      alert("Нет токена авторизации");
      return;
    }

    try {
      const res = await fetch(`${URL}/api/ock-object-docs/${entry.docId}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
      }

      setDocLinks((prev) =>
        prev.filter((link) => String(link.document_id) !== String(entry.docId))
      );
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(entry.id);
        return next;
      });
    } catch (e) {
      console.error("Delete failed:", e);
      alert("Ошибка удаления файла");
    }
  };

  const handleRenameSingle = async (entry) => {
    if (!canManage || entry.type !== "file") return;

    const nextName = window.prompt("Новое имя файла:", entry.name);
    if (!nextName || nextName.trim() === entry.name) return;

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      alert("Нет токена авторизации");
      return;
    }

    try {
      const res = await fetch(`${URL}/api/ock-object-docs/${entry.docId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ name: nextName.trim() }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
      }

      setDocLinks((prev) =>
        prev.map((link) => {
          if (String(link.document_id) !== String(entry.docId)) return link;

          return {
            ...link,
            document_name: data.name ?? link.document_name,
            name: data.name ?? link.name,
            cloud_path: data.cloud_path ?? link.cloud_path,
            file_ext: data.file_ext ?? link.file_ext,
            updated_at: data.updated_at ?? link.updated_at,
            preview_url: data.preview_url ?? link.preview_url,
            download_url: data.download_url ?? link.download_url,
          };
        })
      );
    } catch (e) {
      console.error("Rename failed:", e);
      alert("Ошибка переименования файла");
    }
  };

  const handleBulkDelete = async () => {
    if (!canManage) return;

    const files = selectedEntries.filter((entry) => entry.type === "file");
    if (!files.length) {
      alert("Выберите хотя бы один файл");
      return;
    }

    const uniqueDocIds = [...new Set(files.map((f) => String(f.docId)))];

    const ok = window.confirm(
      `Удалить выбранные файлы (${uniqueDocIds.length}) в корзину? Они исчезнут из всех объектов, где прикреплены.`
    );
    if (!ok) return;

    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      alert("Нет токена авторизации");
      return;
    }

    try {
      const uniqueDocIds = [...new Set(files.map((f) => String(f.docId)))];

      for (const docId of uniqueDocIds) {
        const res = await fetch(`${URL}/api/ock-object-docs/${docId}/`, {
          method: "DELETE",
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
        }
      }

      const deletedDocIds = new Set(uniqueDocIds);
      setDocLinks((prev) =>
        prev.filter((link) => !deletedDocIds.has(String(link.document_id)))
      );
      setSelectedIds(new Set());
    } catch (e) {
      console.error("Bulk delete failed:", e);
      alert("Ошибка пакетного удаления");
    }
  };

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
  
    if (!files.length) return;
  
    if (!currentObjectId) {
      alert("Не удалось определить объект для загрузки");
      return;
    }
  
    const token = tokenProp || localStorage.getItem("auth_token");
    if (!token) {
      alert("Нет токена авторизации");
      return;
    }
  
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
  
    setUploading(true);
  
    try {
      const res = await fetch(`${URL}/api/ock-objects/${currentObjectId}/upload-docs/`, {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
        },
        body: formData,
      });
  
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        throw new Error(typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`);
      }
  
      const created = Array.isArray(data) ? data : data.items || data.results || [];
      setDocLinks((prev) => [...created, ...prev]);
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Ошибка загрузки файлов");
    } finally {
      setUploading(false);
    }
  };

  if (!canView) return <div>Доступно только пользователям группы OCK</div>;
  if (loading) return <div>Загрузка облака документов...</div>;

  return (
    <div className="ock-cloud">
      <h2 className="ock-cloud__title">Облако документов OCK</h2>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileInputChange}
      />
  
      <div className="ock-cloud__headerCard">
        <div className="ock-cloud__breadcrumbs">
          {breadcrumbs.map((crumb, idx) => (
            <React.Fragment key={crumb.path}>
              <button
                type="button"
                className="ock-cloud__crumb"
                onClick={() => openFolder(crumb.path)}
              >
                {crumb.name}
              </button>
  
              {idx < breadcrumbs.length - 1 ? (
                <span className="ock-cloud__crumbSep">/</span>
              ) : null}
            </React.Fragment>
          ))}
        </div>
  
        <div className="ock-cloud__toolbar">
          <div className="ock-cloud__searchWrap">
            <div className="ock-cloud__searchBar">
              <input
                className="ock-cloud__search"
                placeholder="Поиск по имени или пути…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
  
              <button
                className="ock-cloud__clearBtn"
                onClick={clearSearch}
                title="Очистить"
                type="button"
              >
                ✕
              </button>
            </div>
          </div>
  
          <div className="ock-cloud__toolbarActions">
            <button
              className="ock-cloud__btn ock-cloud__btn--secondary"
              type="button"
              onClick={goUp}
              disabled={normalizeSlashes(currentPath) === ROOT_PATH}
            >
              Вверх
            </button>
  
            <button
              className="ock-cloud__btn ock-cloud__btn--primary"
              type="button"
              onClick={openUploadDialog}
              disabled={!canUploadHere || uploading}
              title={
                canUploadHere
                  ? `Загрузить в объект: ${currentObject?.name || currentObjectId}`
                  : "Загрузка доступна только внутри папки объекта OCK"
              }
            >
              {uploading ? "Загрузка..." : "Загрузить"}
            </button>
  
            <button
              className="ock-cloud__btn ock-cloud__btn--danger"
              type="button"
              disabled={!selectedEntries.some((e) => e.type === "file")}
              onClick={handleBulkDelete}
            >
              Удалить выбранные
            </button>
          </div>
        </div>
  
        <div className="ock-cloud__submeta">
          <span>
            Текущая папка: <b>{currentPath}</b>
          </span>
          <span>
            Элементов: <b>{filteredEntries.length}</b>
          </span>
          <span>
            Выбрано: <b>{selectedEntries.length}</b>
          </span>
          {currentObject ? (
            <span>
              Объект загрузки: <b>{currentObject.name}</b>
            </span>
          ) : (
            <span>
              Объект загрузки: <b>не выбран</b>
            </span>
          )}
        </div>
      </div>
  
      <div className="ock-cloud__layout">
        <aside className="ock-cloud__sidebar">
          <div className="ock-cloud__panelTitle">Иерархия</div>
  
          <div className="ock-cloud__scroll">
            <FolderTreeNode
              node={folderTree}
              currentPath={currentPath}
              expandedPaths={expandedPaths}
              onToggle={toggleExpand}
              onOpen={openFolder}
            />
          </div>
        </aside>
  
        <section className="ock-cloud__content">
          {filteredEntries.length === 0 ? (
            <div className="ock-cloud__empty">В этой директории ничего не найдено</div>
          ) : (
            <div className="ock-cloud__scroll">
              <div className="ock-cloud__listHead">
                <label className="ock-cloud__selectAll">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                  />
                  <span>Выбрать всё</span>
                </label>
              </div>
  
              <div className="ock-cloud__list">
                {filteredEntries.map((entry) => {
                  const isSelected = selectedIds.has(entry.id);
                  const isFolder = entry.type === "folder";
  
                  return (
                    <div key={entry.id} className="ock-cloud__item">
                      <div className="ock-cloud__checkboxCol">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(entry.id)}
                          disabled={isFolder}
                        />
                      </div>
  
                      <div className="ock-cloud__text">
                        <div className="ock-cloud__topline">
                          <span className="ock-cloud__titleText">
                            {isFolder ? "📁" : "📄"} {entry.name}
                          </span>
  
                          <span className="ock-cloud__meta">
                            <span className="ock-cloud__pill">
                              {isFolder ? "Папка" : "Файл"}
                            </span>
  
                            {!isFolder ? (
                              <span className="ock-cloud__pill">
                                Размер: {formatSize(entry.size)}
                              </span>
                            ) : null}
                          </span>
                        </div>
  
                        <div className="ock-cloud__desc">
                          <b>Путь:</b> {entry.path}
                        </div>
  
                        <div className="ock-cloud__submeta">
                          {!isFolder ? (
                            <>
                              <span>
                                <b>Загружен:</b> {formatDateTime(entry.created_at)}
                              </span>
                              <span>
                                <b>Обновлён:</b> {formatDateTime(entry.updated_at)}
                              </span>
                              <span>
                                <b>Пользователь:</b> {entry.uploaded_by_username || "—"}
                              </span>
                            </>
                          ) : (
                            <span>
                              <b>Директория-представление:</b> построена из объектов OCK
                            </span>
                          )}
                        </div>
                      </div>
  
                      <div className="ock-cloud__actions">
                        {isFolder ? (
                          <button
                            className="ock-cloud__btn ock-cloud__btn--secondary ock-cloud__miniBtn"
                            type="button"
                            onClick={() => openFolder(entry.path)}
                          >
                            Открыть
                          </button>
                        ) : (
                          <>
                            <button
                              className="ock-cloud__btn ock-cloud__btn--secondary ock-cloud__miniBtn"
                              type="button"
                              onClick={() => handlePreview(entry)}
                            >
                              Просмотр
                            </button>
  
                            <button
                              className="ock-cloud__btn ock-cloud__btn--secondary ock-cloud__miniBtn"
                              type="button"
                              onClick={() => handleDownload(entry)}
                            >
                              Скачать
                            </button>
  
                            <button
                              className="ock-cloud__btn ock-cloud__btn--secondary ock-cloud__miniBtn"
                              type="button"
                              onClick={() => handleRenameSingle(entry)}
                              disabled={!canManage}
                            >
                              Переименовать
                            </button>
  
                            <button
                              className="ock-cloud__btn ock-cloud__btn--danger ock-cloud__miniBtn"
                              type="button"
                              onClick={() => handleDeleteSingle(entry)}
                              disabled={!canManage}
                            >
                              Удалить
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}